import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { X } from "lucide-react";
import { readConsent, writeConsent, injectAnalytics } from "@/lib/analytics";

const CookieConsent = ({ openSignal }) => {
  const { t } = useTranslation();
  const [banner, setBanner] = useState(false);
  const [prefsOpen, setPrefsOpen] = useState(false);
  const [analytics, setAnalytics] = useState(false);

  useEffect(() => {
    const existing = readConsent();
    if (!existing) {
      setBanner(true);
    } else if (existing.choices?.analytics) {
      injectAnalytics();
      setAnalytics(true);
    }
  }, []);

  useEffect(() => {
    if (openSignal > 0) {
      const existing = readConsent();
      setAnalytics(!!existing?.choices?.analytics);
      setPrefsOpen(true);
    }
  }, [openSignal]);

  const acceptAll = () => {
    writeConsent({ necessary: true, analytics: true });
    setAnalytics(true);
    setBanner(false);
    setPrefsOpen(false);
  };
  const rejectNonEssential = () => {
    writeConsent({ necessary: true, analytics: false });
    setAnalytics(false);
    setBanner(false);
    setPrefsOpen(false);
  };
  const savePrefs = () => {
    writeConsent({ necessary: true, analytics });
    setBanner(false);
    setPrefsOpen(false);
  };

  return (
    <>
      {banner && !prefsOpen && (
        <div
          role="dialog"
          aria-live="polite"
          aria-label="Cookie consent"
          className="fixed inset-x-0 bottom-0 z-40 bg-white border-t border-[var(--border)] shadow-[0_-4px_16px_rgba(0,0,0,0.05)]"
          data-testid="cookie-banner"
        >
          <div className="mx-auto max-w-[1240px] px-6 lg:px-10 py-5 flex flex-col md:flex-row items-start md:items-center gap-4">
            <p className="text-sm text-[var(--charcoal)] flex-1">
              {t("cookies.banner_text")}{" "}
              <Link
                to="/cookie-policy"
                className="text-[var(--pharma-blue)] underline underline-offset-2"
              >
                {t("cookies.policy_link")}
              </Link>
            </p>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => setPrefsOpen(true)}
                className="btn-ghost text-xs"
                data-testid="cookie-preferences-btn"
              >
                {t("cookies.preferences")}
              </button>
              <button
                type="button"
                onClick={rejectNonEssential}
                className="btn-outline text-xs"
                data-testid="cookie-reject-btn"
              >
                {t("cookies.reject")}
              </button>
              <button
                type="button"
                onClick={acceptAll}
                className="btn-primary text-xs"
                data-testid="cookie-accept-btn"
              >
                {t("cookies.accept_all")}
              </button>
            </div>
          </div>
        </div>
      )}

      {prefsOpen && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label={t("cookies.modal_title")}
          className="fixed inset-0 z-50 flex items-end md:items-center justify-center"
          data-testid="cookie-modal"
        >
          <div
            className="absolute inset-0 bg-black/40"
            onClick={() => setPrefsOpen(false)}
          />
          <div className="relative w-full md:max-w-lg bg-white border border-[var(--border)] shadow-lg">
            <div className="flex items-center justify-between px-6 py-4 border-b border-[var(--border)]">
              <h2 className="font-heading text-base font-semibold text-[var(--navy)]">
                {t("cookies.modal_title")}
              </h2>
              <button
                type="button"
                onClick={() => setPrefsOpen(false)}
                aria-label="Close"
                className="p-1 text-[var(--slate)]"
                data-testid="cookie-modal-close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="px-6 py-5 space-y-5">
              <p className="text-sm text-[var(--slate)]">
                {t("cookies.modal_intro")}
              </p>
              <div className="flex items-start justify-between gap-4 py-3 border-t border-[var(--border)]">
                <div>
                  <div className="text-sm font-medium text-[var(--charcoal)]">
                    {t("cookies.cat_necessary")}
                  </div>
                  <p className="text-xs text-[var(--slate)] mt-1">
                    {t("cookies.cat_necessary_desc")}
                  </p>
                </div>
                <input
                  type="checkbox"
                  checked
                  disabled
                  aria-label={t("cookies.cat_necessary")}
                  className="mt-1"
                  data-testid="cookie-toggle-necessary"
                />
              </div>
              <div className="flex items-start justify-between gap-4 py-3 border-t border-[var(--border)]">
                <div>
                  <div className="text-sm font-medium text-[var(--charcoal)]">
                    {t("cookies.cat_analytics")}
                  </div>
                  <p className="text-xs text-[var(--slate)] mt-1">
                    {t("cookies.cat_analytics_desc")}
                  </p>
                </div>
                <input
                  type="checkbox"
                  checked={analytics}
                  onChange={(e) => setAnalytics(e.target.checked)}
                  aria-label={t("cookies.cat_analytics")}
                  className="mt-1"
                  data-testid="cookie-toggle-analytics"
                />
              </div>
            </div>
            <div className="px-6 py-4 border-t border-[var(--border)] flex flex-wrap justify-end gap-2">
              <button
                type="button"
                onClick={rejectNonEssential}
                className="btn-outline text-xs"
                data-testid="cookie-modal-reject"
              >
                {t("cookies.reject")}
              </button>
              <button
                type="button"
                onClick={savePrefs}
                className="btn-primary text-xs"
                data-testid="cookie-modal-save"
              >
                {t("cookies.save")}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CookieConsent;
